import { Post } from "@configs/Axios";

export interface AnalyticsFilterRule {
  deviceId: string;
  ruleName: string;
  channel: number;
}

export interface AnalyticsFilterRules {
  [key: string]: AnalyticsFilterRule[];
}

export interface AnalyticsFilterApiInputPayload {
  orgId: string;
  startDateTime: string;
  endDateTime: string;
  timeInterval: string;
  rules: AnalyticsFilterRules;
  vehicleType?: string[];
  cumulativeType: string;
  hasSingleLocation?: boolean;
}

export interface AnalyticsCSVDownloadPayload {
  orgId: string;
  startDateTime: string;
  endDateTime: string;
  widgetType: string;
  rules: AnalyticsFilterRules;
  hasSingleLocation?: boolean;
  language?: string;
}

export interface AnalyticsCategoryApiInputPayload
  extends Omit<AnalyticsFilterApiInputPayload, "cumulativeType"> {
  category: string;
}

export type AnalyticsDetailsResponse =
  | AnalyticsDataResponse
  | AnalyticPeopleFlowResponse
  | AnalyticQueuesResponse
  | AnalyticStoppedVehicleResponse;

export type AnalyticsChartDataResponse = (AnalyticsDetailsResponse & {
  widgetType?: string;
})[];
export interface AnalyticsDataResponse {
  locationName: string;
  total?: number;
  chartData: number[];
  color: string;
  originalData?: { count: number; time: number; level?: number }[];
  originalStoppedVehicleData?: StoppedVehicleChartData;
  key: string;
  cameraName?: string;
}

export type AnalyticQueueDataResponse = AnalyticQueuesResponse &
  AnalyticsDataResponse;

export interface AnalyticPeopleFlowResponse {
  locationName: string;
  cameraName?: string;
  in: {
    chartData: number[];
  };
  out: {
    chartData: number[];
  };
}

export interface QueueCountResponse {
  count: number;
  time: number;
}
export interface AnalyticQueuesResponse {
  locationName: string;
  cameraName?: string;
  chartData: QueueCountResponse[];
  originalData: QueueCountResponse[];
  averageQueueCount?: number;
  averageDwellTime?: number;
}

export interface StoppedVehicleChartData {
  bicycle: number[];
  bus: number[];
  car: number[];
  motorcycle: number[];
  truck: number[];
}

export interface AnalyticStoppedVehicleResponse {
  locationName: string;
  cameraName?: string;
  chartData: StoppedVehicleChartData;
  total: number;
}

export interface AnalyticsCategoryData {
  idx: number;
  count: number;
  intervalName: string;
  rate?: number;
  time?: number;
  totalCount?: number;
}

export type AnalyticsCategoryDataResponse = AnalyticsCategoryData[];

export const analyticsApi = {
  chartAnalytics: async (
    widgetType: string,
    {
      orgId,
      startDateTime,
      endDateTime,
      rules,
      timeInterval,
      vehicleType,
      cumulativeType,
      hasSingleLocation
    }: AnalyticsFilterApiInputPayload
  ) => {
    const type = widgetType.includes("cumulative")
      ? `&cumulativeType=${cumulativeType.toUpperCase()}`
      : "";
    const apiUrl = `/v3/organizations/${orgId}/${widgetType}?startDateTime=${encodeURIComponent(
      startDateTime
    )}&endDateTime=${encodeURIComponent(
      endDateTime
    )}&timeInterval=${timeInterval}${
      vehicleType && vehicleType.length > 0
        ? vehicleType
            .map(
              (type) =>
                `&vehicleType=${encodeURIComponent(type.toLocaleUpperCase())}`
            )
            .join("")
        : ""
    }${type}`;

    return Post<AnalyticsChartDataResponse>(apiUrl, {
      rules,
      hasSingleLocation
    });
  },
  categoryAnalytics: async (
    widgetType: string,
    {
      orgId,
      startDateTime,
      endDateTime,
      rules,
      category
    }: AnalyticsCategoryApiInputPayload
  ) => {
    const apiUrl = `/v3/organizations/${orgId}/${widgetType}?startDateTime=${encodeURIComponent(
      startDateTime
    )}&endDateTime=${encodeURIComponent(endDateTime)}
    `;

    return Post<AnalyticsCategoryDataResponse>(apiUrl, { rules, category });
  },
  csvDownloadAnalytics: async ({
    orgId,
    startDateTime,
    endDateTime,
    language,
    ...req
  }: AnalyticsCSVDownloadPayload) => {
    return Post<string>(
      `/v1/organizations/${orgId}/analytics/export?startDateTime=${encodeURIComponent(
        startDateTime
      )}&endDateTime=${encodeURIComponent(endDateTime)}&lang=${language}`,
      req
    );
  }
};
